/* global google */
import React, { useEffect, useRef, useState } from "react";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import "./showAreas.css";
import { useNavigate } from "react-router-dom";


const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

export default function GeoJsonViewer() {
  const mapRef = useRef(null);
  const navigate = useNavigate();


  const [totalApps, setTotalApps] = useState(0);

  // 🆕 state برای نگهداری اطلاعات پروژه انتخاب شده
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    const loadMapScript = () =>
      new Promise((resolve) => {
        if (window.google) return resolve();
        const script = document.createElement("script");
        script.src =
          "https://maps.googleapis.com/maps/api/js?key=" + GOOGLE_API_KEY;
        script.onload = resolve;
        document.body.appendChild(script);
      });

    const init = async () => {
      await loadMapScript();

      const map = new google.maps.Map(mapRef.current, {
        center: { lat: 43.9, lng: -79.4 },
        zoom: 10,
      });

      const geoJsonUrl =
        "https://services1.arcgis.com/GzvOwaQBbX7KLiuG/arcgis/rest/services/Hosted_Development_Application_Status_And_Teams/FeatureServer/0/query?where=1%3D1&outFields=*&f=geojson";

      const typeColors = {
        "Site Plan": "#FF5733",
        "Draft Plan of Condominium": "#33C1FF",
        "Consent to Sever": "#3370ff",
        "Official Plan": "#33FF57",
        "Zoning By-law Amendment": "#FFC300",
        "Minor Variances": "#ff0000",
        "Draft Plan of Subdivision": "#FF33A8",
        "Local Official Plan Amendment": "#33ff41",
        "Engineering Application": "#00979a",
        "Block Plan": "#003b3c",
        "Provincial Application": "#490031",
        default: "#999999",
      };

      const response = await fetch(geoJsonUrl);
      const data = await response.json();

      const markers = [];
      const bounds = new google.maps.LatLngBounds();
      const activeIDs = new Set();

      const getCentroid = (coords) => {
        let lng = 0,
          lat = 0,
          count = 0;
        coords[0].forEach(([x, y]) => {
          lng += x;
          lat += y;
          count++;
        });
        return { lat: lat / count, lng: lng / count };
      };

      data.features.forEach((f) => {
        const p = f.properties;

        const isActive =
          p.APPL_STATUS === "In Progress" || p.APPL_STATUS === "Finalization";
        if (!isActive) return;

        activeIDs.add(p.APPL_GISID);

        const centroid =
          f.geometry.type === "Polygon"
            ? getCentroid(f.geometry.coordinates)
            : f.geometry.type === "MultiPolygon"
              ? getCentroid(f.geometry.coordinates[0])
              : null;

        if (!centroid) return;


        const color = typeColors[p.APPL_TYPE] || typeColors.default;

        const svgPin = `
          <svg width="40" height="40" viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg">
            <path fill="${color}" 
              d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5
              c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5s2.5 1.1 2.5 2.5
              -1.1 2.5-2.5 2.5z"/>
          </svg>
        `;

        const marker = new google.maps.Marker({
          position: centroid,
          map,
          icon: {
            url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svgPin),
            scaledSize: new google.maps.Size(36, 36),
            anchor: new google.maps.Point(18, 36),
          },
        });


        // ⬅️ به جای InfoWindow، اطلاعات را در پنل سمت چپ نشان بده
        marker.addListener("click", () => {
          setSelectedProject({
            id: p.SFORCEID,   // ← این را اضافه کن

            name: p.APPL_NAME,
            status: p.APPL_STATUS,
            type: p.APPL_TYPE,
            desc: p.APPL_DESC,
            loc: p.LOCATION,
            coordinator: p.ConstructionCoordinator,
            stage: p.APPL_STAGE,
            file: p.REG_FILE_NUM,
            step: p.APPL_STEP,
            recived: formatDate(p.DATE_RECVD),         // ← تاریخ به‌صورت درست
            lastSubmission: p.Submission_Date__c,
            address: p.ADDRESS,
            landUse: p.LANDUSE_DESIG,
            munName: p.MUN_NAME,
            area: p.Shape__Area,


            centroid: {
              lat: centroid.lat,
              lng: centroid.lng,
            },
          });
        });

        markers.push(marker);
        bounds.extend(centroid);
      });

      new MarkerClusterer({
        map,
        markers,
        maxZoom: 13,
        minimumClusterSize: 2,
      });

      map.fitBounds(bounds);
      setTotalApps(activeIDs.size);
    };

    init();
  }, []);


  const formatDate = (ts) => {
    if (!ts || isNaN(ts)) return "N/A";
    const d = new Date(Number(ts));
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-CA"); // YYYY-MM-DD
  };


  return (
    <div style={{ width: "100%", position: "relative" }}>
      {/* TOP BAR */}
      <div className="top-bar">
        Active Development Applications of York Region
      </div>

      {/* 🆕 DETAILS PANEL (سمت چپ) */}
      {selectedProject && (
        <div className="details-panel">
          <div className="details-title">Application Details</div>

          <div><strong>Name:</strong> {selectedProject.name}</div>
          <div><strong>municipality:</strong> {selectedProject.munName}</div>
          <div><strong>Type of Application:</strong> {selectedProject.type}</div>
          <div><strong>Date Received:</strong> {selectedProject.recived}</div>
          <div><strong>Status:</strong> {selectedProject.status}</div>
          <div><strong>Stage:</strong> {selectedProject.stage}</div>
          <div><strong>File #:</strong> {selectedProject.file}</div>
          <div><strong>Location:</strong> {selectedProject.loc}</div>
          <div><strong>Description:</strong> {selectedProject.desc}</div>
          {/* name: p.APPL_NAME,
          status: p.APPL_STATUS,
          type: p.APPL_TYPE,
          desc: p.APPL_DESC,
          loc: p.LOCATION,
          coordinator: p.ConstructionCoordinator,
          stage: p.APPL_STAGE,
          file: p.REG_FILE_NUM,
          step:p.APPL_STEP,
          recived:p.DATE_RECVD,
          lastSubmission:p.Submission_Date__c,
          address:p.ADDRESS,
          landUse:p.LANDUSE_DESIG,
          munName:p.MUN_NAME,
          area:p.Shape__Area */}








          <button
            className="close-btn"
            onClick={() => setSelectedProject(null)}
          >
            Close
          </button>

          <button
            className="analyze-btn"
            onClick={() =>
              navigate(`/projects/${selectedProject.id}`, {
                state: {
                  centroid: selectedProject.centroid,
                },
              })
            }
          >
            Analyze Project →
          </button>

        </div>
      )}

      {/* LEGEND */}
      <div className="legend-box">
        <div className="legend-title">Application Types</div>

        {[
          ["Site Plan", "#FF5733"],
          ["Draft Plan of Condominium", "#33C1FF"],
          ["Consent to Sever", "#3370ff"],
          ["Official Plan", "#33FF57"],
          ["Zoning By-law Amendment", "#FFC300"],
          ["Minor Variances", "#ff0000"],
          ["Draft Plan of Subdivision", "#FF33A8"],
          ["Local Official Plan Amendment", "#33ff41"],
          ["Engineering Application", "#00979a"],
          ["Block Plan", "#003b3c"],
          ["Provincial Application", "#490031"],
        ].map(([label, color]) => (
          <div className="legend-item" key={label}>
            <div className="legend-color" style={{ background: color }}></div>
            {label}
          </div>
        ))}
      </div>

      {/* MAP */}
      <div ref={mapRef} className="map-container" />
    </div>
  );
}
